import { londonLastMonthRange, londonMonthToDateRange } from './ordersDailyPresentation';
import { ukTodayYmd, ymdWeekday } from './ukTime';

/**
 * The Control Centre period chips that the Driver tab and the hub (Fleet
 * Alerts) share. Each is a stored Driver tab period in zeecore
 * (driverTabStoredView.ts `driverTabWindows`), which computes the same
 * windows to the millisecond — a request for one of these is served from
 * storage. Change both together.
 */
export type ControlCentrePeriod =
  | 'today'
  | 'yesterday'
  | 'last-week'
  | 'last-full-week'
  | 'month-to-date'
  | 'last-month'
  | 'last_30_days'
  | 'last_180_days';

const dayStart = (ymd: string) => new Date(`${ymd}T00:00:00.000Z`);
const dayEnd = (ymd: string) => new Date(`${ymd}T23:59:59.999Z`);

/** `days` calendar days before UK today. */
function utcDaysAgo(now: Date, days: number): string {
  const d = new Date(`${ukTodayYmd(now)}T12:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Days since this week's Monday, by UK date (0 on a Monday). */
function ukDaysSinceMonday(now: Date): number {
  return WEEKDAYS.indexOf(ymdWeekday(ukTodayYmd(now)));
}

/**
 * Inclusive window for a period chip. "Today" is the UK date; each day is
 * written as its date with UTC-midnight bounds, which is the UK day for
 * Aquarius rows (UK clock stored as UTC). Month chips use the London month.
 */
export function controlCentrePeriodRange(period: ControlCentrePeriod, now: Date = new Date()): { start: Date; end: Date } {
  const today = ukTodayYmd(now);
  switch (period) {
    case 'today':
      return { start: dayStart(today), end: dayEnd(today) };
    case 'yesterday': {
      const y = utcDaysAgo(now, 1);
      return { start: dayStart(y), end: dayEnd(y) };
    }
    case 'last-week':
      return { start: dayStart(utcDaysAgo(now, 7)), end: dayEnd(today) };
    case 'last-full-week': {
      // Monday to Sunday of the last complete week.
      const sinceMonday = ukDaysSinceMonday(now);
      return { start: dayStart(utcDaysAgo(now, sinceMonday + 7)), end: dayEnd(utcDaysAgo(now, sinceMonday + 1)) };
    }
    case 'month-to-date': {
      const { start, end } = londonMonthToDateRange(now);
      return { start: dayStart(start), end: dayEnd(end) };
    }
    case 'last-month': {
      const { start, end } = londonLastMonthRange(now);
      return { start: dayStart(start), end: dayEnd(end) };
    }
    case 'last_30_days':
      return { start: dayStart(utcDaysAgo(now, 30)), end: dayEnd(today) };
    case 'last_180_days':
      return { start: dayStart(utcDaysAgo(now, 180)), end: dayEnd(today) };
  }
}
